import * as React from 'react';
import { Modal, Steps } from 'antd';
import { checkForExtension } from 'utils/checkForBugherdExtension';
import { OnboardingSteps, ChecklistEnum } from './model';
import styles from './index.module.css';
import TryBugHerd from './TryBugHerd';
import ProjectSetup from './ProjectSetup';
import InstallBugherd from './InstallBugherd';
import { getOnboardingState } from './state';
import StepTitle from './StepTitle';
import * as translations from './strings';
import { getLangKey } from 'models/Language';
import RightPanel from './RightPanel';
import { trackStep } from './analytics';
import track, { ONBOARDING_CREATE_PROJECT } from 'utils/analytics';
import cx from 'classnames';
import { InviteTeam } from './InviteTeam';

const { Step } = Steps;

interface OnboardingPropsType {
  showOnboarding: boolean;
  projectName: string;
  projectUrl: string;
  isJavascriptInstalled: boolean;
  isWebsiteVisited: boolean;
  isExtensionInstalled: boolean;
  organizationId: number;
  userId: number;
  projectId: number;
  experiments: string[];
  onCloseModal: () => void;
  apiKey: string;
  javascriptInstalledOverride: boolean;
  bugherdUrl: string;
  hasInvitedMember: boolean;
  skippedInviteMember: boolean;
}

const Onboarding: React.FC<OnboardingPropsType> = ({
  showOnboarding,
  projectUrl,
  projectName,
  projectId,
  organizationId,
  userId,
  isJavascriptInstalled,
  isWebsiteVisited,
  isExtensionInstalled,
  apiKey,
  bugherdUrl,
  experiments,
  onCloseModal,
  hasInvitedMember,
  skippedInviteMember,
  ...props
}) => {
  const strings = translations[getLangKey()];
  const [
    javascriptInstalledOverride,
    setJavascriptInstalledOverride
  ] = React.useState<boolean>(props.javascriptInstalledOverride);
  const [visibility, setVisibility] = React.useState<boolean>(showOnboarding);
  const [checkExtensionInstall, setExtensionInstalled] = React.useState<
    boolean
  >(isExtensionInstalled);
  const [currentProjectUrl, setProjectUrl] = React.useState<string>(projectUrl);
  const [currentProjectName, setProjectName] = React.useState<string>(
    projectName
  );
  const inviteMemberOnboarding = experiments.includes(
    'invitemember_onboarding_variant'
  );

  const currentState = getOnboardingState({
    name: currentProjectName,
    url: currentProjectUrl,
    isJavascriptInstalled: isJavascriptInstalled,
    isWebsiteVisited: isWebsiteVisited,
    isExtensionInstalled: checkExtensionInstall,
    javascriptInstalledOverride,
    inviteMemberOnboarding
  });

  const [visibleStep, setVisibleStep] = React.useState<ChecklistEnum>(
    currentState
  );

  const [showEmbedJavascript, setShowEmbedJavascript] = React.useState<boolean>(
    false
  );

  const hideModal = () => {
    setVisibility(false);
    onCloseModal();
  };

  React.useEffect(() => {
    (async () => {
      const exists = await checkForExtension();

      if (exists) {
        setExtensionInstalled(exists);
      }
    })();
  }, [isExtensionInstalled]);

  React.useEffect(() => {
    setVisibleStep(currentState);

    if (isWebsiteVisited) {
      if (!inviteMemberOnboarding || hasInvitedMember || skippedInviteMember)
        return hideModal();
      setVisibleStep(ChecklistEnum.INVITE_TEAM);
    }
  }, [
    isExtensionInstalled,
    isJavascriptInstalled,
    checkExtensionInstall,
    isWebsiteVisited,
    currentProjectUrl,
    currentProjectName,
    javascriptInstalledOverride
  ]);

  React.useEffect(() => {
    trackStep(visibleStep);
  }, [visibleStep]);

  const container = document.getElementById('empty_state_container');

  return (
    <Modal
      className={cx(styles.onbModal, 'onbModal')}
      open={visibility}
      getContainer={container}
      footer={null}
      closable={false}
    >
      <div>
        <div className={styles.titleModal}>
          <h2>{strings.learnToUse(inviteMemberOnboarding)} </h2>
        </div>
        <div className={styles.container}>
          <div className={styles.leftPanel}>
            <Steps
              className={styles.step}
              size={'small'}
              direction="vertical"
              current={currentState}
            >
              <Step
                key={OnboardingSteps[ChecklistEnum.SETUP_PROJECT].title}
                title={
                  <StepTitle
                    title={OnboardingSteps[ChecklistEnum.SETUP_PROJECT].title}
                    visibleStepTitle={
                      OnboardingSteps[ChecklistEnum.SETUP_PROJECT]
                        .visibleStepTitle
                    }
                    activeTitle={strings.setupProjectTitleDone(
                      currentProjectName
                    )}
                    isVisibleStep={visibleStep === ChecklistEnum.SETUP_PROJECT}
                    isActive={currentState >= ChecklistEnum.SETUP_PROJECT}
                    stepOnClick={() =>
                      setVisibleStep(ChecklistEnum.SETUP_PROJECT)
                    }
                  />
                }
                description={
                  visibleStep === ChecklistEnum.SETUP_PROJECT && (
                    <ProjectSetup
                      currentProjectName={currentProjectName}
                      currentProjectUrl={currentProjectUrl}
                      projectId={projectId}
                      onStepSuccess={(
                        projectName: string,
                        projectUrl: string
                      ) => {
                        // Shared onboarding funnel event — fires in both the
                        // control flow (here) and the Q2 2026 variant's Step 1.
                        track(ONBOARDING_CREATE_PROJECT, {
                          experiments: experiments.join(',')
                        });
                        setProjectUrl(projectUrl);
                        setProjectName(projectName);
                        const nextState = getOnboardingState({
                          name: projectName,
                          url: projectUrl,
                          isJavascriptInstalled,
                          isWebsiteVisited,
                          isExtensionInstalled: checkExtensionInstall,
                          javascriptInstalledOverride,
                          inviteMemberOnboarding
                        });
                        setVisibleStep(nextState);
                      }}
                    />
                  )
                }
              />
              <Step
                key={OnboardingSteps[ChecklistEnum.INSTALL_BH].title}
                title={
                  <StepTitle
                    title={OnboardingSteps[ChecklistEnum.INSTALL_BH].title}
                    visibleStepTitle={
                      showEmbedJavascript
                        ? strings.installJavascriptCode
                        : OnboardingSteps[ChecklistEnum.INSTALL_BH]
                            .visibleStepTitle
                    }
                    isVisibleStep={visibleStep === ChecklistEnum.INSTALL_BH}
                    activeTitle={
                      OnboardingSteps[ChecklistEnum.INSTALL_BH].activeTitle
                    }
                    isActive={currentState >= ChecklistEnum.INSTALL_BH}
                    stepOnClick={() => setVisibleStep(ChecklistEnum.INSTALL_BH)}
                  />
                }
                description={
                  visibleStep === ChecklistEnum.INSTALL_BH && (
                    <InstallBugherd
                      project={{
                        apiKey,
                        id: String(projectId),
                        javascriptInstalledOverride
                      }}
                      bugherdUrl={bugherdUrl}
                      onInstallCallback={() =>
                        setJavascriptInstalledOverride(true)
                      }
                      goToNextStep={() => setVisibleStep(ChecklistEnum.WEBSITE)}
                      isOnboarding
                      showEmbedJavascript={showEmbedJavascript}
                      setShowEmbedJavascript={setShowEmbedJavascript}
                    />
                  )
                }
              />
              <Step
                key={OnboardingSteps[ChecklistEnum.WEBSITE].title}
                title={
                  <StepTitle
                    title={OnboardingSteps[ChecklistEnum.WEBSITE].title}
                    visibleStepTitle={
                      OnboardingSteps[ChecklistEnum.WEBSITE].visibleStepTitle
                    }
                    isVisibleStep={visibleStep === ChecklistEnum.WEBSITE}
                    activeTitle={
                      OnboardingSteps[ChecklistEnum.WEBSITE].activeTitle
                    }
                    isActive={currentState >= ChecklistEnum.WEBSITE}
                    stepOnClick={() => setVisibleStep(ChecklistEnum.WEBSITE)}
                  />
                }
                description={
                  visibleStep === ChecklistEnum.WEBSITE && (
                    <TryBugHerd
                      projectUrl={currentProjectUrl}
                      projectId={projectId}
                      experiments={experiments}
                    />
                  )
                }
              />
              {inviteMemberOnboarding && (
                <Step
                  key={OnboardingSteps[ChecklistEnum.INVITE_TEAM].title}
                  title={
                    <StepTitle
                      title={OnboardingSteps[ChecklistEnum.INVITE_TEAM].title}
                      visibleStepTitle={
                        OnboardingSteps[ChecklistEnum.INVITE_TEAM]
                          .visibleStepTitle
                      }
                      isVisibleStep={visibleStep === ChecklistEnum.INVITE_TEAM}
                      activeTitle={
                        OnboardingSteps[ChecklistEnum.INVITE_TEAM].activeTitle
                      }
                      isActive={currentState >= ChecklistEnum.INVITE_TEAM}
                      stepOnClick={() =>
                        setVisibleStep(ChecklistEnum.INVITE_TEAM)
                      }
                    />
                  }
                  description={
                    visibleStep === ChecklistEnum.INVITE_TEAM && (
                      <InviteTeam
                        projectId={projectId}
                        organizationId={organizationId}
                        onFinish={hideModal}
                        currentUserId={userId}
                      />
                    )
                  }
                />
              )}
            </Steps>
          </div>
          <div className={styles.rightPanel}>
            <RightPanel
              organizationId={organizationId}
              userId={userId}
              step={visibleStep}
              showEmbedJavascript={showEmbedJavascript}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Onboarding;
